import { type ReactNode, useEffect } from 'react'
import { useLocation, Link as RouterLink } from 'react-router'
import Link from '@mui/material/Link'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import PageWrapper from 'common-ui/components/PageWrapper'
import PageBreadcrumbs, { type BreadcrumbItem } from 'common-ui/components/PageBreadcrumbs'
import { Path } from '../../routes/path'
import { useSidebar } from './SidebarContext'

interface WrapperProps {
    actions?: ReactNode
    children: ReactNode
    title?: string
}

interface RouteConfig {
    path: string
    title: string
    parent?: string
}

const routes: Record<string, RouteConfig> = {
    profile: { path: Path.profile, title: 'Profile' },
    customers: { path: Path.customers, title: 'Customers' },
    createCustomer: { path: Path.createCustomer, title: 'Create customer', parent: 'Customers' },
    editCustomer: { path: '/customers/', title: 'Edit customer', parent: 'Customers' },
    licenses: { path: Path.licenses, title: 'Licenses' },
    createLicense: { path: Path.createLicense, title: 'Create license', parent: 'Licenses' },
    editLicense: { path: '/licenses/', title: 'View license', parent: 'Licenses' },
    installations: { path: Path.installations, title: 'Installations' },
    installationDetails: { path: '/customer/', title: 'Installation details', parent: 'Installations' },
    appliances: { path: Path.appliances, title: 'Appliances' },
    applianceDetails: { path: '/appliances/', title: 'Appliance details', parent: 'Appliances' },
    billing2: { path: Path.billing2, title: 'Billing' },
    billing: { path: Path.billing, title: 'Billing (legacy)' },
    billingDetails: { path: Path.billingDetails, title: 'Billing details', parent: 'Billing (legacy)' },
    ai: { path: Path.ai, title: 'AI logs' },
    aiSettings: { path: Path.aiSettings, title: 'AI settings' },
    aiConversation: { path: `${Path.ai}/`, title: 'Conversation', parent: 'AI logs' },
    users: { path: Path.users, title: 'Users' },
    internalUsers: { path: Path.internalUsers, title: 'Internal users' },
    createUser: { path: Path.createUser, title: 'Create internal user', parent: 'Internal users' },
    editUser: { path: `${Path.internalUsers}/`, title: 'Edit internal user', parent: 'Internal users' },
    externalUsers: { path: Path.externalUsers, title: 'External users' },
    createExternalUser: {
        path: Path.createExternalUser,
        title: 'Create external user',
        parent: 'External users',
    },
    externalUser: { path: `${Path.externalUsers}/`, title: 'External user', parent: 'External users' },
    auditLog: { path: Path.auditLog, title: 'Audit log' },
    settings: { path: Path.settings, title: 'Global settings' },
}

function getPageBreadcrumbs(pathname: string): BreadcrumbItem[] {
    const route =
        Object.values(routes).find((r) => r.path === pathname) ||
        Object.values(routes).find((r) => pathname.startsWith(r.path) && r.parent)

    if (!route) return []

    const crumbs: BreadcrumbItem[] = []
    if (route.parent) {
        const parentRoute = Object.values(routes).find((r) => r.title === route.parent)
        crumbs.push({ label: route.parent, link: parentRoute?.path })
    }
    crumbs.push({ label: route.title })
    return crumbs
}

export default function Wrapper({ actions, children, title }: WrapperProps) {
    const location = useLocation()
    const { toggle } = useSidebar()
    const breadcrumbs = getPageBreadcrumbs(location.pathname)

    if (title && breadcrumbs.length > 0) {
        breadcrumbs[breadcrumbs.length - 1] = { label: title }
    }

    const pageTitle = breadcrumbs
        .map((b) => b.label)
        .toReversed()
        .join(' - ')
    useEffect(() => {
        document.title = pageTitle ? `${pageTitle} - Billing Manager` : 'Billing Manager'
    }, [pageTitle])

    return (
        <PageWrapper
            toolbarLeft={
                <IconButton edge="start" color="inherit" aria-label="Toggle sidebar" onClick={toggle}>
                    <MenuIcon />
                </IconButton>
            }
            title={
                <PageBreadcrumbs
                    items={breadcrumbs}
                    renderLink={({ to, children, 'data-testid': dataTestId }) => (
                        <Link component={RouterLink} to={to} data-testid={dataTestId} color="inherit" underline="hover">
                            {children}
                        </Link>
                    )}
                />
            }
            actions={actions}
        >
            {children}
        </PageWrapper>
    )
}
